import { config } from "@/lib/config";
import {
  ChangePasswordRequest,
  ConfirmEmailChangeRequest,
  ConfirmEmailRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  ProfileResponse,
  RegisterAgentRequest,
  RegisterUserRequest,
  RegisterResponse,
  RequestEmailChangeRequest,
  ResendEmailConfirmationRequest,
  ResetPasswordRequest,
  VerificationStatusRequest,
} from "@/types/IdentityTypes";
import { ApiBase } from "./ApiBase";

const API_BASE_URL = config.VITE_IDENTITY_API_URL;

export enum LoginErrorCode {
  InvalidCredentials = "InvalidCredentials",
}

export enum ResetPasswordErrorCode {
  ResetCodeRequired = "ResetCodeRequired",
  CodeInvalid = "InvalidCode",
}

export enum RegisterErrorCode {
  DuplicateEmail = "DuplicateEmail",
  PersonalEmailNotAllowed = "PersonalEmailNotAllowed",
}

export enum VerifyEmailErrorCode {
  CodeInvalid = "InvalidToken",
}

export enum ChangePasswordErrorCode {
  PasswordChangeNotAllowed = "PasswordChangeNotAllowed",
  PasswordMismatch = "PasswordMismatch",
}

export enum EmailChangeErrorCode {
  EmailChangeNotAllowed = "EmailChangeNotAllowed",
  DuplicateEmail = "DuplicateEmail",
  PasswordMismatch = "PasswordMismatch",
  SameEmail = "SameEmail",
}

export class WrongLoginCredentialsError extends Error {
  constructor() {
    super("Wrong email or password.");
  }
}

export class InvalidResetPasswordCodeError extends Error {
  constructor() {
    super("Invalid reset password code.");
  }
}

export class EmailAlreadyExistsError extends Error {
  constructor() {
    super("Email already exists.");
  }
}

export class PersonalEmailNotAllowedError extends Error {
  constructor() {
    super(
      "Please use your corporate email to sign up as an agent. Personal email addresses are not allowed.",
    );
  }
}

export class InvalidConfirmEmailCodeError extends Error {
  constructor() {
    super("Invalid confirmation code.");
  }
}

export class InvalidVerificationStatusError extends Error {
  constructor() {
    super("Invalid verification status.");
  }
}

export class PersonaInquiryError extends Error {
  constructor(message: string = "Failed to create verification inquiry.") {
    super(message);
  }
}

export class PasswordChangeNotAllowedError extends Error {
  constructor() {
    super("Password changes are managed through UAE Pass for verified users.");
  }
}

export class PasswordMismatchError extends Error {
  constructor() {
    super("Current password is incorrect.");
  }
}

export class SameEmailError extends Error {
  constructor() {
    super("New email must be different from your current email.");
  }
}

export class EmailChangeNotAllowedError extends Error {
  constructor() {
    super("Email changes are managed through UAE Pass for verified users.");
  }
}

class IdentityApi extends ApiBase {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return this.post<LoginRequest, LoginResponse>(
      `${API_BASE_URL}/api/identity/login`,
      credentials,
      {
        domainErrors: {
          [LoginErrorCode.InvalidCredentials]: WrongLoginCredentialsError,
        },
      },
    );
  }

  async getProfile(): Promise<ProfileResponse> {
    return this.get<ProfileResponse>(`${API_BASE_URL}/api/identity/user-info`);
  }

  async registerAgent(
    request: RegisterAgentRequest,
  ): Promise<RegisterResponse> {
    return this.post<RegisterAgentRequest, RegisterResponse>(
      `${API_BASE_URL}/api/identity/register`,
      request,
      {
        domainErrors: {
          [RegisterErrorCode.PersonalEmailNotAllowed]:
            PersonalEmailNotAllowedError,
          [RegisterErrorCode.DuplicateEmail]: EmailAlreadyExistsError,
        },
      },
    );
  }

  async registerUser(request: RegisterUserRequest): Promise<RegisterResponse> {
    return this.post<RegisterUserRequest, RegisterResponse>(
      `${API_BASE_URL}/api/identity/register-user`,
      request,
      {
        domainErrors: {
          [RegisterErrorCode.DuplicateEmail]: EmailAlreadyExistsError,
        },
      },
    );
  }

  async logout(logoutId?: string): Promise<LogoutResponse> {
    return this.get<LogoutResponse>(
      `${API_BASE_URL}/api/identity/logout?logoutId=${logoutId || ""}`,
    );
  }

  async confirmEmail(request: ConfirmEmailRequest): Promise<void> {
    return this.post<ConfirmEmailRequest, void>(
      `${API_BASE_URL}/api/identity/confirm-email`,
      request,
      {
        domainErrors: {
          [VerifyEmailErrorCode.CodeInvalid]: InvalidConfirmEmailCodeError,
        },
      },
    );
  }

  async resetPassword(request: ResetPasswordRequest): Promise<void> {
    return this.post<ResetPasswordRequest, void>(
      `${API_BASE_URL}/api/identity/reset-password`,
      request,
      {
        domainErrors: {
          [ResetPasswordErrorCode.CodeInvalid]: InvalidResetPasswordCodeError,
        },
      },
    );
  }

  async forgotPassword(request: ForgotPasswordRequest): Promise<void> {
    return this.post<ForgotPasswordRequest, void>(
      `${API_BASE_URL}/api/identity/forgot-password`,
      request,
    );
  }

  async resendEmailConfirmation(
    request: ResendEmailConfirmationRequest,
  ): Promise<void> {
    return this.post<ResendEmailConfirmationRequest, void>(
      `${API_BASE_URL}/api/identity/resend-email-confirmation`,
      request,
    );
  }

  async updateVerificationStatus(
    request: VerificationStatusRequest,
  ): Promise<void> {
    return this.post<VerificationStatusRequest, void>(
      `${API_BASE_URL}/api/identity/update-verification-status`,
      request,
      {
        domainErrors: {
          InvalidVerificationStatus: InvalidVerificationStatusError,
        },
      },
    );
  }

  async updateProfile(request: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
  }): Promise<void> {
    return this.post<typeof request, void>(
      `${API_BASE_URL}/api/identity/profile`,
      request,
    );
  }

  async updateEmiratesId(emiratesId: string): Promise<void> {
    return this.post<{ emiratesId: string }, void>(
      `${API_BASE_URL}/api/identity/emirates-id`,
      { emiratesId },
    );
  }

  async changePassword(request: ChangePasswordRequest): Promise<void> {
    return this.post<ChangePasswordRequest, void>(
      `${API_BASE_URL}/api/identity/change-password`,
      request,
      {
        domainErrors: {
          [ChangePasswordErrorCode.PasswordChangeNotAllowed]:
            PasswordChangeNotAllowedError,
          [ChangePasswordErrorCode.PasswordMismatch]: PasswordMismatchError,
        },
      },
    );
  }

  async requestEmailChange(request: RequestEmailChangeRequest): Promise<void> {
    return this.post<RequestEmailChangeRequest, void>(
      `${API_BASE_URL}/api/identity/request-email-change`,
      request,
      {
        domainErrors: {
          [EmailChangeErrorCode.EmailChangeNotAllowed]:
            EmailChangeNotAllowedError,
          [EmailChangeErrorCode.DuplicateEmail]: EmailAlreadyExistsError,
          [EmailChangeErrorCode.PasswordMismatch]: PasswordMismatchError,
          [EmailChangeErrorCode.SameEmail]: SameEmailError,
        },
      },
    );
  }

  async confirmEmailChange(request: ConfirmEmailChangeRequest): Promise<void> {
    return this.post<ConfirmEmailChangeRequest, void>(
      `${API_BASE_URL}/api/identity/confirm-email-change`,
      request,
      {
        domainErrors: {
          [EmailChangeErrorCode.EmailChangeNotAllowed]:
            EmailChangeNotAllowedError,
          [EmailChangeErrorCode.DuplicateEmail]: EmailAlreadyExistsError,
        },
      },
    );
  }

  // Persona verification methods
  createInquiry(): Promise<{ inquiryId: string; sessionToken: string }> {
    return this.post<
      Record<string, never>,
      { inquiryId: string; sessionToken: string }
    >(`${API_BASE_URL}/api/identity/verification/inquiry`, {});
  }

  resumeInquiry(): Promise<{ sessionToken: string }> {
    return this.post<Record<string, never>, { sessionToken: string }>(
      `${API_BASE_URL}/api/identity/verification/resume`,
      {},
    );
  }

  getInquiryStatus(): Promise<{
    inquiryId: string | null;
    status: string;
    isVerified: boolean;
  }> {
    return this.get<{
      inquiryId: string | null;
      status: string;
      isVerified: boolean;
    }>(`${API_BASE_URL}/api/identity/verification/status`);
  }
}

export const identityApi = new IdentityApi();
